/**
 * TutorialTree — grouped tutorial list for the Tutorials page.
 *
 * Props:
 *   highlightId  {string}   — tutorial id to auto-scroll and highlight
 *   onPlay       {fn}       — called with tutorial object when Watch is clicked
 *
 * Data shape: a node is either a leaf ({ items: [...] }) or a branch
 * ({ children: [...nodes] }) — arbitrary depth, rendered recursively.
 *
 * Structure: "Platform Basics" is generic, cross-strategy trust/orientation
 * content — not a strategy pitch. Each of the three strategies (Pyramid
 * Hedging, Futures Pyramid Hedging, Weekly Index Short Straddle) then gets
 * its own complete, self-contained top-level section running basic →
 * advanced: concept, comparisons/positioning, real trading examples,
 * backtests, and finally a "Get Started" sub-group (import starter rules →
 * backtest them → practice in demo mode) that closes out that strategy's
 * track. There is no separate "reference library" tier — a strategy's full
 * depth lives in one place. Everything after the three strategy tracks is
 * strategy-agnostic: "Go Live" (SnapTrade, or the advanced self-hosted
 * gateway, as two children of one section), "Monitoring & Execution" (which
 * also folds in Account Settings — day-to-day workflow and the global config
 * behind it, together), and "Rules Deep Dive" (custom rule-building beyond
 * the starter bundle). See docs/TUTORIAL_ONBOARDING_PLAN.md for the
 * rationale.
 */

import React, { useRef, useEffect, useState } from "react";

// Runtime white-label config — set by nginx-mounted /app-config.js (see BrandingSettingsPage)
const appName = (window.APP_CONFIG || {}).appName || "Fincrest Systems";

/* ── Tutorial data — platform basics + three parallel strategy tracks ───── */
export const TUTORIALS = [
  /* ─────────────────────────────────────────────────────────────────────
   * 1 — PLATFORM BASICS (generic — not strategy-specific)
   * ───────────────────────────────────────────────────────────────────── */
  {
    series: "Platform Basics",
    color: "#545cd8",
    icon: "📚",
    description: "Start here. Why manual options management doesn't scale, how the platform stays in your control, and how to find your way around — before you pick a strategy.",
    items: [
      {
        id: "P01", title: "The Multi-Account Problem", duration: "90s", priority: "P0",
        desc: "Managing options manually across multiple accounts is a scaling problem. See exactly where it breaks.",
        topics: [
          "Why manual management doesn't scale beyond one account",
          "The consistency gap: same stock, different decisions across accounts",
          "What rule-based automation actually solves at scale",
        ],
        videoSrc: "/tutorials/platform/p01-multi-account.mp4",
      },
      {
        id: "P02", title: "Why Manual Options Management Fails", duration: "90s", priority: "P0",
        desc: "Even for a single account, manual options management has a structural inconsistency problem. Here's the evidence.",
        topics: [
          "Decision fatigue at expiry — why the last week is the hardest",
          "Inconsistent roll timing and what it costs over 12 months",
          "The compounding cost of reactive management vs systematic rules",
        ],
        videoSrc: "/tutorials/platform/p02-manual-options.mp4",
      },
      {
        id: "T01", title: "Trust & Control", duration: "60s", priority: "P0",
        desc: "The system never places an order without your explicit approval. See exactly how the human-in-the-loop approval flow works — from rule fire to executed trade.",
        topics: [
          "Human-in-the-loop design: why every suggestion requires explicit approval before any order is placed",
          "How to approve, reject, or snooze any suggestion — and what each choice means",
          "Full audit trail: every decision, every order, every outcome recorded permanently",
        ],
        videoSrc: "/tutorials/platform/t01-approval.mp4",
      },
      {
        id: "00", title: `Welcome to ${appName}`, duration: "52s", priority: "P0",
        desc: `A quick overview of what ${appName} does, the strategies it supports, and why rule-based automation changes the options trading game.`,
        topics: [
          `What ${appName} automates: rule-based options and futures management across three strategies`,
          "The rule engine cycle: how it evaluates your rules every 5 minutes and queues suggestions",
          "Human-in-the-loop: every suggestion requires your approval before any order executes",
        ],
        videoSrc: "/tutorials/platform/00-welcome.mp4",
      },
      {
        id: "01", title: "Dashboard Walkthrough", duration: "90s", priority: "P0",
        desc: "A full tour of the main dashboard: P&L cards, strategy status, position summary, and how to navigate between the platform's core pages.",
        topics: [
          "Total P&L, stock P&L, option P&L, and potential P&L — what each card means",
          "Active strategies and active rules badges: what triggers a number change",
          "Short option value and open positions at a glance",
          "Quick-action buttons: jump directly to suggestions, rules, positions, or strategy builder",
        ],
        videoSrc: "/tutorials/platform/01-dashboard.mp4",
      },
      {
        id: "03", title: "Understanding the Rule Engine", duration: "2:39 min", priority: "P0",
        desc: "The conceptual foundation shared by every strategy: how facts, conditions, and actions work together to drive every automated decision the system makes.",
        topics: [
          "Facts: ltp, next_buy_price, stock_pct_drop, hedge_pct — recalculated every 5-minute cycle",
          "Conditions: AND/OR logic trees that evaluate facts against thresholds",
          "Actions: what the engine suggests — BUY_STOCK, ROLL_DOWN_CALL, ROLL_OUT_CALL, and more",
          "Groups and priority: how the engine picks one winner when multiple rules trigger together",
          "Cooldowns and group_max_fires: preventing queue flooding on rapid price moves",
        ],
        videoSrc: "/tutorials/platform/03-rules.mp4",
      },
    ],
  },

  /* ─────────────────────────────────────────────────────────────────────
   * 2 — PYRAMID HEDGING (main strategy — full track, basic → advanced)
   * ───────────────────────────────────────────────────────────────────── */
  {
    series: "Pyramid Hedging",
    color: "#7c3aed",
    icon: "📈",
    description: "The main strategy on the platform, start to finish: how it works, how it compares to strategies you may already run, every setting, real backtest proof, and the hands-on steps to import rules, backtest, and practice before going live.",
    children: [
      {
        series: "Strategy Guide",
        color: "#7c3aed",
        icon: "📘",
        description: "The core concept, the Strategy Builder UI, every setting, and the philosophy behind buying up instead of down.",
        items: [
          {
            id: "C00", title: "Pyramid Hedging: The Swiss-Army Strategy", duration: "2 min", priority: "P0",
            desc: "One engine, four configurations. This video maps Pyramid Hedging's setup options onto the covered call, the Wheel, and even the short strangle — the core concept behind everything you'll build.",
            topics: [
              "Base configuration (ladder + single call hedge) ≈ a modified covered call",
              "+ Cash-Secured Put ≈ the Wheel, with two income engines from day one",
              "+ CSP + Income-First Mode ≈ a short strangle — same income objective, plus a profit floor",
              "A roadmap to the comparison videos below",
            ],
            videoSrc: "/tutorials/pyramid_hedging/c00-swiss-army.mp4",
          },
          {
            id: "G01", title: "Why We Buy Up, Not Down", duration: "114s", priority: "P0",
            desc: "Every investing instinct says buy the dip. This video makes the structural case for why buying on confirmed upward momentum — not on falling prices — is fundamentally different from cost-averaging.",
            topics: [
              "Momentum confirmation vs hope-based buying: the key structural difference",
              "How upward ladder buys reinforce the call hedge naturally as the position grows",
              "The pause: what happens when the stock stalls between rungs",
              "Why Pyramid Hedging never averages down — and why that matters for risk",
            ],
            videoSrc: "/tutorials/pyramid_hedging/g01-why-we-buy-up.mp4",
          },
          {
            id: "02", title: "Building Your First Strategy", duration: "4:47 min", priority: "P0",
            desc: "A step-by-step walkthrough of the Strategy Builder UI — from picking a symbol and expiry to reading the output and activating your strategy.",
            topics: [
              "Configuring the ladder: step gap, position size cap, hedge style, and the Cash-Secured Put leg",
              "Adding a ticker, setting the expiry date, and walking through each settings field",
              "Reading the builder output: what the ladder preview, call strike, and projected income show",
              "Saving vs activating — what each does and when you'd do one without the other",
              "What happens in the first 5-minute cycle after activation",
            ],
            videoSrc: "/tutorials/platform/02-strategy.mp4",
          },
          {
            id: "PH-S", title: "Pyramid Hedging: Settings Guide", duration: "~9 min", priority: "P1",
            desc: "A complete walkthrough of every Pyramid Hedging setting, organized as 4 chapters. By the end you'll understand the maths behind the ladder, know how to position your hedge for bullish or income-focused setups, and have both income engines — call and CSP — working for you.",
            topics: [
              "Chapter 1 — The Ladder: step gap floor, budget cap, and the effective average cost formula with worked NVDA example",
              "Chapter 2 — Hedge Style: target_strike = projected_avg ÷ slider; 0.90 (bullish, OTM call) vs 1.10 (income, tight call)",
              "Chapter 3 — Income-First Mode: flip the optimizer to hit your monthly income target before expanding the ladder",
              "Chapter 4 — The CSP: safety buffer, rollup trigger, and why disabling it removes ~40% of total strategy income",
            ],
            videoSrc: "/tutorials/pyramid_hedging/ph_settings_guide.mp4",
          },
        ],
      },
      {
        series: "Comparisons",
        color: "#7c3aed",
        icon: "⚖️",
        description: "How Pyramid Hedging stacks up against strategies you may already run — same market exposure, different mechanics.",
        items: [
          {
            id: "C01", title: "Covered Call vs. Pyramid Hedging", duration: "105s", priority: "P0",
            desc: "The classic covered call collects premium but caps upside permanently. PH adapts the hedge as the position grows.",
            topics: [
              "Static vs adaptive call placement — what changes as the stock rises",
              "What happens when the stock rallies past a fixed call strike",
              "How PH collects premium without a permanent upside ceiling",
            ],
            videoSrc: "/tutorials/pyramid_hedging/c01-covered-call.mp4",
          },
          {
            id: "C02", title: "Cash-Secured Put vs. Pyramid Hedging", duration: "105s", priority: "P0",
            desc: "CSPs buy on assignment — catching falling stocks. PH buys on confirmed upward moves. The direction matters.",
            topics: [
              "Buying on weakness vs buying on strength — same instrument, opposite signal",
              "Assignment risk in CSPs and what it does to your cost basis",
              "How PH uses the CSP as a monthly income stream, not an entry signal",
            ],
            videoSrc: "/tutorials/pyramid_hedging/c02-csp.mp4",
          },
          {
            id: "C03", title: "DCA / Averaging vs. Pyramid Hedging", duration: "90s", priority: "P0",
            desc: "DCA deploys capital on a schedule. PH deploys capital when the market proves momentum. Same goal, opposite signal.",
            topics: [
              "Time-based vs price-confirmation deployment — why the signal changes risk",
              "The averaging-down trap: why deploying on weakness feels safe but isn't",
              "How momentum confirmation changes the probability of each buy",
            ],
            videoSrc: "/tutorials/pyramid_hedging/c03-dca.mp4",
          },
          {
            id: "C04", title: "Wheel Strategy vs. Pyramid Hedging", duration: "105s", priority: "P0",
            desc: "The Wheel is income-first. PH is accumulation-first with income as a byproduct. Two different objectives.",
            topics: [
              "The Wheel's assignment cycle and why it relies on the stock falling",
              "Why PH doesn't need assignment — it accumulates on upward moves",
              "Income generation without needing the stock to drop first",
            ],
            videoSrc: "/tutorials/pyramid_hedging/c04-wheel.mp4",
          },
          {
            id: "C05", title: "Short Strangles & Iron Condors vs. Pyramid Hedging", duration: "2:37 min", priority: "P0",
            desc: "Strangles and iron condors collect the biggest premiums in options trading — and carry open-ended risk on both sides. This video introduces how Pyramid Hedging with CSP and Income-First Mode pursues the same income with a pre-defined profit floor.",
            topics: [
              "Why both sides of a strangle carry open-ended risk — and why iron condor wings don't fully fix it",
              "The active-management burden: delta watching, ITM response, two expiry clocks",
              "Income-First Mode's profit floor: the Minimum Cycle Profit Target",
              "Automated delta-hedge roll-down keeps the position balanced as the stock moves",
            ],
            videoSrc: "/tutorials/pyramid_hedging/c05-strangles-iron-condors.mp4",
          },
        ],
      },
      {
        series: "Trading Setups",
        color: "#7c3aed",
        icon: "🧩",
        description: "Cash-Secured Put is its own toggle — Income-First Mode isn't crossed with it to make new configurations, since Income-First Mode is now the default for every new strategy (see the Settings Guide) and applies the same way whether or not CSP is on. Two real setups: without the CSP leg, and with it. Each covers the concept, the settings, which rule-template categories to import, and backtested proof across a trending, a falling, and a flat/choppy market.",
        children: [
          {
            series: "Setup 1: Cash-Secured Put Disabled",
            color: "#7c3aed",
            icon: "1️⃣",
            description: "Stock and a single call hedge, no put leg — approximates a modified covered call. The simpler two-leg structure, whether Income-First Mode is on (the default) or off.",
            items: [
              {
                id: "TE01", title: "Setup 1 — Concept: The Call-Only Structure", duration: "~2 min", priority: "P1",
                desc: "The simplest version of Pyramid Hedging: stock and a call, no put leg. A real NVDA account shows what call-only income looks like — and why it barely clears a profit when the ladder's average cost drifts above the strike.",
                topics: [
                  "Call-only structure: two legs instead of three, no CSP buying-power reserved",
                  "Real NVDA numbers: $222.50 call strike, ~$276/month premium, Reference Avg Price $225.09",
                  "Why Tail P&L lands at just $17 — the stock leg's small loss almost offsets the call premium",
                  "When call-only is the right choice: simplicity, or limited buying power",
                ],
                videoSrc: "/tutorials/pyramid_hedging/te01-csp-disabled.mp4",
              },
              {
                id: "TE03", title: "Setup 1 — Income-First Mode in Action: OFF", duration: "~2 min", priority: "P1",
                desc: "The same NVDA account with Income-First Mode off. Only Hedge Style steers the ladder here, with no profit target doing any work — shown as a real reference point for how the toggle behaves, not as this setup's recommended starting point (Income-First Mode is on by default — see the next video).",
                topics: [
                  "Hedge Style (average buy price ÷ call strike) is the only thing governing gap width in this mode",
                  "Minimum Ladder Step Gap is a floor, not the answer — Hedge Style can and does widen it further",
                  "Real NVDA numbers: ~1.5% gap, $222.50 call strike, Tail P&L just $17 — trivial by design",
                  "Why this mode was never asked to guarantee a profit number, only to respect the ratio",
                ],
                videoSrc: "/tutorials/pyramid_hedging/te03-income-first-mode-off.mp4",
              },
              {
                id: "TE04", title: "Setup 1 — Income-First Mode in Action: ON (The Default)", duration: "~2 min", priority: "P1",
                desc: "Same NVDA account, one toggle flipped: the optimizer stops asking how wide Hedge Style allows the gap to go, and starts asking how little capital it needs to guarantee your profit floor. Shown here without CSP to isolate exactly what the toggle does on its own.",
                topics: [
                  "Minimum Cycle Profit Target: the real number this mode is built to defend",
                  "Selection order: fewest lots wins first, then the widest gap that still protects the floor",
                  "Real numbers: $230 call strike, gap narrows to ~1.25%, Tail P&L $1,022 — clearing the $1,000 target by the smallest safe margin, not the biggest",
                  "Why ON doesn't always mean a wider gap — it depends which constraint binds tighter",
                ],
                videoSrc: "/tutorials/pyramid_hedging/te04-income-first-mode-on.mp4",
              },
              {
                id: "TS1-RULES", title: "Setup 1 — Rules to Add", duration: "~2 min", priority: "P1",
                desc: "Which starter-rule-template categories a two-leg, no-CSP strategy needs — with and without Income-First Mode on.",
                topics: [
                  "Buy / Ladder Entry, Roll Out, Roll Up & Call Protection, Exit on Profit, Expiry Handling — the full set this setup needs regardless of the Income-First toggle",
                  "With Income-First Mode on (the default): Roll Down's ROLL_DOWN_ON_MINIMUM_CYCLE_PROFIT_TARGET_BREACHED becomes load-bearing, and every rollout's min_profit_csm check starts mattering",
                  "With it off: that rule stays dormant and Roll Out's decay/DTE thresholds do all the work instead",
                  "What NOT to import either way: Cash-Secured Put Management — this setup has no put leg",
                ],
                videoSrc: null,
              },
              {
                id: "TS1-BT-TREND", title: "Setup 1 — Backtest: Trending Market", duration: "~2 min", priority: "P1",
                desc: "A real Setup 1 (CSP disabled) backtest on CRM, up 33% in under a month, run with Income-First Mode on — script is ground-truth (job 545da2c1), video not yet recorded. The rally never actually tests the hedge: Income-First Mode picks a strike so far out of the money on day one that not a single roll fires the whole month.",
                topics: [
                  "Income-First Mode picks the strike's width, not the Hedge Style ratio — 31% out of the money on day one, versus the ~5% a plain hedge ratio would pick",
                  "Zero rolls the entire month — the cushion between price and strike narrows from 31% to under 6% by the last rung, but the strike is never crossed",
                  "Reading the Adjustments Log: six BUY_PYRAMID rungs, no roll events at all",
                  "Where the P&L actually comes from — 1,365 of 1,368 dollars is the stock leg; the call's entire contribution is 2 dollars 33 cents of thin premium",
                ],
                videoSrc: null,
              },
              {
                id: "BT02", title: "Setup 1 — Backtest: Falling Market (MSFT, The Crash)", duration: "~4 min", priority: "P1",
                desc: "A real one-month Setup 1 backtest — CSP disabled — on MSFT during a sharp ~16% mid-month decline, showing how the hedge defends itself through repeated expiry rolls and how the ROLL_ON_EBP_BREACH rule catches trouble even before the crash starts. Recorded under this account's own settings (Income-First off) predating the current default — still real, valid proof of the call-only structure's downside behavior.",
                topics: [
                  "rocho_demo's actual settings: CSP disabled, Income-First off, 1% min ladder gap, $2,000/rung, 1.05 hedge style",
                  "ROLL_ON_EBP_BREACH fires at the month's peak — before the crash, using effective buy price (EBP), not just raw LTP",
                  "The call hedge rolled out to fresh expiries multiple times as each near-worthless contract decayed",
                  "End-of-month summary: a real $529 stock loss reduced to a $23 wash",
                ],
                videoSrc: "/tutorials/pyramid_hedging/bt02_msft_one_month_backtest.mp4",
              },
              {
                id: "TS1-BT-FLAT", title: "Setup 1 — Backtest: Flat / Choppy Market", duration: "~2 min", priority: "P1",
                desc: "Coming soon. A real Setup 1 backtest on a stock chopping sideways within a narrow range, run with Income-First Mode on — this is BT03's AAPL parameter sweep's headline result in disguise (see the wrap-up video below): the same Income-First OFF vs ON pairing that turned a $1,523 loss into a $710 gain, replayed here as this setup's dedicated flat-market example.",
                topics: [
                  "Same core lever as the AAPL parameter sweep, reframed as this setup's flat-market proof",
                  "Why a sideways, uncapped-short-call environment is exactly where a profit-target-driven ladder earns its keep",
                  "No new ladder rungs firing — price never clears the next buy point — and how the profit floor still gets defended",
                ],
                videoSrc: null,
              },
            ],
          },
          {
            series: "Setup 2: Cash-Secured Put Enabled",
            color: "#7c3aed",
            icon: "2️⃣",
            description: "Adds a cash-secured put to the same ladder and call hedge — two income engines instead of one. With Income-First Mode also on (the default), approximates a short strangle with a built-in profit floor and automated delta-hedge defense.",
            items: [
              {
                id: "TE02", title: "Setup 2 — Concept & Settings: Cash-Secured Put Enabled", duration: "~2 min", priority: "P1",
                desc: "Same NVDA account, same call — flip on the Cash-Secured Put and total profit at completion more than doubles. A real before/after comparison showing exactly where the extra income comes from.",
                topics: [
                  "CSP Safety Buffer and CSP Rollup Trigger Distance settings explained with real values",
                  "Real before/after: Tail P&L $198.92 (call only) → $453.79 (call + CSP)",
                  "Why the entire uplift equals the put's own premium — nothing else in the structure changes",
                  "Buying-power check: strike × 100 × lots reserved as collateral before enabling",
                ],
                videoSrc: "/tutorials/pyramid_hedging/te02-csp-enabled.mp4",
              },
              {
                id: "C06", title: "Setup 2 — Deep Dive: How Pyramid Hedging Out-Engineers the Strangle", duration: "4 min", priority: "P1",
                desc: "A deep dive into the mechanics behind the strangle comparison's claim: how the ladder is sized to guarantee a profit floor on the upside, and how automated delta-hedge rolls defend against losses on the downside — including the worst case where the CSP goes in-the-money.",
                topics: [
                  "Tail P&L: the one number that drives ladder size — stock gain + call premium + put premium at full-ladder completion",
                  "How Income-First Mode searches for the smallest ladder that clears the Minimum Cycle Profit Target",
                  "Once the ladder completes, the position becomes a covered call — the profit floor is locked in regardless of how high the stock goes",
                  "delta_difference and ROLL_DOWN_CALL: automated delta-neutral rebalancing every 5-minute cycle",
                  "The ITM-put rescue: rolling the CSP toward ATM to restore the profit floor",
                ],
                videoSrc: "/tutorials/pyramid_hedging/c06-income-first-mode.mp4",
              },
              {
                id: "TS2-RULES", title: "Setup 2 — Rules to Add", duration: "~2 min", priority: "P1",
                desc: "Everything from Setup 1, plus what enabling the put leg switches on — with Income-First Mode's floor-defense rules layered on top since that's the default.",
                topics: [
                  "Cash-Secured Put Management (ROLLDOWN_ITM_PUT) — rolls the CSP down if it goes in the money",
                  "Roll Out's ROLLOUT_OPTIONS now carries both legs — call and CSP roll out together, not just the call",
                  "Roll Up & Call Protection's ROLLUP_PUT_BULLISH_STOCK — rescues the put's unrealized profit when the call side is underwater but the stock is bullish",
                  "Exit on Profit's PUT_CLOSE_PROFIT, plus Roll Down's ROLL_DOWN_ON_DELTA_IMBALANCE — the delta-neutral rebalancer that matters most once both legs are live",
                ],
                videoSrc: null,
              },
              {
                id: "TS2-BT-TREND", title: "Setup 2 — Backtest: Trending Market", duration: "~2:25 min", priority: "P1",
                desc: "A real one-month Setup 2 backtest on NVDA (CSP enabled, confirmed settings) — call and CSP both sold on Day 0 before a single share is bought, replayed from the live Backtest page with the actual Adjustments Log behind every claim. Source script is ground-truth; the currently shipped file predates it and needs a re-record before this slot goes live — see the note in the onboarding plan.",
                topics: [
                  "Day one: both legs open immediately — SELL_INITIAL_CALL and SELL_INITIAL_CSP, zero shares held yet",
                  "2% min ladder gap, $2,000/rung, 1.15 hedge ratio — call strike sits 15% above entry",
                  "The ladder builds through a rally while both option legs keep collecting and rolling",
                  "Final tally read straight from the Adjustments Log and P&L card",
                ],
                videoSrc: null,
              },
              {
                id: "TS2-BT-FALL", title: "Setup 2 — Backtest: Falling Market", duration: "~2 min", priority: "P1",
                desc: "Coming soon. A real Setup 2 backtest during a genuine decline — the scenario that exercises the full stack at once: CSP rolling toward the money, the profit-target rule under pressure, and delta-hedge rebalancing trying to hold the floor.",
                topics: [
                  "ROLL_DOWN_ON_MINIMUM_CYCLE_PROFIT_TARGET_BREACHED and the ITM-put rescue path firing together",
                  "How much of the profit floor actually survives a real drawdown, with real numbers",
                  "Comparison against Setup 1's falling-market result — what the CSP leg adds when things go wrong, not just when they go right",
                ],
                videoSrc: null,
              },
              {
                id: "TS2-BT-FLAT", title: "Setup 2 — Backtest: Flat / Choppy Market", duration: "~2 min", priority: "P1",
                desc: "Coming soon. A real Setup 2 backtest in a sideways market with a shallow dip that never breaches the roll-down threshold — the strategy simply holds both legs and collects. Candidate source: the NVDA one-month backtest's \"dip, no roll\" stretch (May 15 – June 15) once re-recorded to match the current script.",
                topics: [
                  "The dip that doesn't trigger a roll — call and CSP both stay safely away from their strikes",
                  "Pure premium collection with no adjustments firing — the calmer of the two setups' flat-market results",
                  "Closing comparison against Setup 1's flat-market outcome, side by side",
                ],
                videoSrc: null,
              },
            ],
          },
          {
            series: "Prove It Yourself",
            color: "#7c3aed",
            icon: "📊",
            description: "One backtest that doesn't belong to a single setup — it sweeps across settings instead, as a wrap-up once you've seen both configurations on their own.",
            items: [
              {
                id: "BT03", title: "Backtest: Pyramid Hedging — AAPL, Same Strategy, Six Settings", duration: "~2:45 min", priority: "P1",
                desc: "Same stock, same six-week window, same starting capital — this backtest holds the price path fixed and changes one Pyramid Hedging setting at a time across six runs, isolating what each parameter is actually worth in real dollars. Its core pairing (Income-First OFF vs ON) is the same lever as Setup 1's two Income-First videos above.",
                topics: [
                  "Six real backtest runs on AAPL, April 14 – June 19, one setting varied per run",
                  "Companion to the Settings Guide: that video explains what each setting does, this one proves what it's worth",
                  "The core pairing: Income-First OFF (−$1,523.33) vs ON (+$710.88), otherwise identical — the same swing referenced in Setup 1's flat-market slot above",
                  "All numbers verified against real backtest_jobs settings — no seeded or mock data",
                ],
                videoSrc: "/tutorials/pyramid_hedging/bt03_aapl_parameter_sweep.mp4",
              },
            ],
          },
        ],
      },
      {
        series: "Get Started",
        color: "#f7b924",
        icon: "⚙️",
        description: "Now that you understand the strategy: import a bare-minimum working rule set, backtest it, then practice risk-free before you go live.",
        items: [
          {
            id: "S02", title: "Importing the Getting Started Rule Set", duration: "~2 min", priority: "P0",
            desc: "Open the rule template browser, find the green \"Recommended for new strategies\" bundle, and import it in one click — the fastest way to have a working, safe rule set for your first strategy.",
            topics: [
              "The template browser: categories, per-rule descriptions, and the plain-English \"what this does\" view",
              "The Getting Started bundle: pre-selected, pre-expanded, ready to import as-is",
              "Cherry-picking additional individual rules from other categories once you're comfortable",
              "Confirming your rules are ACTIVE in the Rule Builder after import",
            ],
            videoSrc: "/tutorials/pyramid_hedging/s02-import-starter-rules.mp4",
          },
          {
            id: "S03", title: "Backtesting Your Starter Rules", duration: "~2 min", priority: "P0",
            desc: "A walkthrough of the Backtest page using the exact rule set you imported in the previous step — pick a symbol and date range, run it, and read the Adjustments Log and P&L to see exactly what fired and why.",
            topics: [
              "Picking a symbol, strategy, and date range on the Backtest Config page",
              "Auto-Run vs. interactive step-by-step approval of each suggestion",
              "Reading the Adjustments Log: every simulated trade, in order, with the rule that triggered it",
              "The P&L and ladder view: how to tell if a rule set is behaving the way you expect",
            ],
            videoSrc: null,
          },
          {
            id: "S04", title: "Practicing on Your Demo Account", duration: "~2 min", priority: "P0",
            desc: "How demo mode works: real historical US market data from roughly a day ago, replaying at the same 5-minute cycle pace a live account runs on, so suggestions and alerts show up the way they would in real trading — with zero real-money risk.",
            topics: [
              "Why it's ~1 day old, not fully live: real Tradier market data, replayed rather than streamed",
              "Same 5-minute cycle as a live account — suggestions and alerts arrive at a realistic pace, not instantly",
              "Timestamps just show in your browser's local time — nothing to configure",
              "Demo provisioning is set up for you today (not yet self-service) — reach out if you need a fresh demo account",
            ],
            videoSrc: null,
          },
        ],
      },
    ],
  },

  /* ─────────────────────────────────────────────────────────────────────
   * 3 — FUTURES PYRAMID HEDGING (advanced variant — full parallel track)
   * ───────────────────────────────────────────────────────────────────── */
  {
    series: "Futures Pyramid Hedging",
    color: "#0891b2",
    icon: "🪜",
    description: "The advanced version of Pyramid Hedging — extends the same accumulation ladder to index and single-stock futures, with a configurable per-future hedge ratio and two independent expiry cycles. Same start-to-finish shape as Pyramid Hedging: concept, comparisons, real examples, backtests, then the hands-on steps to import rules, backtest, and practice before going live.",
    children: [
      {
        series: "Strategy Guide",
        color: "#0891b2",
        icon: "📘",
        description: "How Futures Pyramid Hedging works, how it compares to the stock version, and every setting explained.",
        items: [
          {
            id: "FPH-I", title: "Introduction to Futures Pyramid Hedging", duration: "~3 min", priority: "P0",
            desc: "Futures Pyramid Hedging takes the same momentum-confirmed accumulation ladder from Pyramid Hedging and applies it to index or single-stock futures — building toward a fully covered position, hedged entirely by short weekly calls.",
            topics: [
              "The hedge ratio: calls_per_future short call lots back every futures contract — and it can be more than one call",
              "Why each ladder rung is exactly one futures contract — no capital search, just confirmed momentum",
              "Calls-only hedge: no CSP/put leg, unlike Pyramid Hedging's stock variant",
              "Two independent expiry cycles: the weekly call hedge rolls separately from the futures contract's own monthly expiry",
            ],
            videoSrc: "/tutorials/futures_pyramid_hedging/fph-intro.mp4",
          },
          {
            id: "FPH-SYN", title: "Synthetic Stock: The Alternative to Futures", duration: "~3 min", priority: "P0",
            desc: "Most individual US stocks have no listed futures contract. This video explains how Futures Pyramid Hedging replicates the ladder's underlying leg with a same-strike long call + short put instead — using put-call parity — and how that combo is rolled and defended differently from a real futures contract.",
            topics: [
              "Put-call parity: why a long call minus a short put at the same strike moves like the stock itself",
              "Why the common strike sits slightly below spot — keeping assignment risk on the OTM short put, not the ITM long call",
              "Two different calls: the long call inside the synthetic combo vs. the short call hedge (calls_per_future) sold on top of it",
              "Rolling short-dated options vs. a real futures contract's monthly cycle — the backstop and defensive triggers behind ROLL_SYNTHETIC_STOCK",
            ],
            videoSrc: null,
          },
          {
            id: "C07", title: "Futures Pyramid Hedging vs. Pyramid Hedging", duration: "~2 min", priority: "P1",
            desc: "Same accumulation philosophy, different instrument. This video breaks down exactly what changes when the ladder is built on futures contracts instead of cash equity shares — leverage, lot-sized rungs, and a second expiry to manage.",
            topics: [
              "Equity shares vs. futures contracts: leverage, margin, and lot-quantized rungs",
              "Why the futures ladder drops the CSP leg and runs calls-only",
              "A fixed per-future hedge ratio (calls_per_future, can be > 1) vs. Pyramid Hedging's call + CSP hedge",
              "Managing two expiries: the weekly call hedge and the monthly futures rollover",
            ],
            videoSrc: null,
          },
          {
            id: "C08", title: "Index Futures vs. Single-Stock Futures", duration: "~2 min", priority: "P1",
            desc: "Futures Pyramid Hedging works on index futures and single-stock futures alike. This video covers how to choose between them — lot sizes, liquidity, and what stays the same regardless of which you pick.",
            topics: [
              "Index futures: broad market exposure, standardized contract specs",
              "Single-stock futures: concentrated exposure to a name you already follow, smaller notional per lot",
              "Lot size and margin differences — and why the ladder math adapts automatically",
              "Choosing the right underlying for your account size and conviction",
            ],
            videoSrc: null,
          },
          {
            id: "FPH-S", title: "Futures Pyramid Hedging: Settings Guide", duration: "~7 min", priority: "P1",
            desc: "A complete walkthrough of every Futures Pyramid Hedging setting, organized as 3 chapters. By the end you'll understand the calls_per_future hedge ratio, the ladder spacing and length search, Cash Saving Mode, and how the two expiry cycles are managed independently.",
            topics: [
              "Chapter 1 — The Hedge Ratio: calls_per_future (can be more than one call per future), Min/Max Hedge %, and how it's derived from the delta scan",
              "Chapter 2 — Ladder Spacing & Cash Saving Mode: minimum step gap, Min Profit CSM, and the tail-P&L search",
              "Chapter 3 — Two Expiries: weekly call rolls (down/out/up) vs. the futures contract's own monthly rollover",
            ],
            videoSrc: "/tutorials/futures_pyramid_hedging/fph-settings-guide.mp4",
          },
        ],
      },
      {
        series: "Trading Examples",
        color: "#0891b2",
        icon: "🧪",
        description: "Real futures accounts, before/after comparisons for the settings that matter most.",
        items: [
          {
            id: "TE-FPH01", title: "Trading Example: Futures Pyramid Hedging — Coming Soon", duration: "~2 min", priority: "P2",
            desc: "A real futures account showing how one Futures Pyramid Hedging setting changes the outcome — live-verified before/after numbers, same format as the Pyramid Hedging Trading Examples.",
            topics: [
              "Setting TBD: likely calls_per_future (hedge ratio) or Cash Saving Mode, mirroring the stock-strategy examples",
              "Same account, same futures contract, one toggle changed",
              "Real before/after numbers — no seeded or hypothetical data",
            ],
            videoSrc: null,
          },
        ],
      },
      {
        series: "Backtests",
        color: "#0891b2",
        icon: "📊",
        description: "Real historical price data, every ladder rung and call hedge roll replayed.",
        items: [
          {
            id: "BT-FPH01", title: "Backtest: Futures Pyramid Hedging — Coming Soon", duration: "~2 min", priority: "P2",
            desc: "A real Futures Pyramid Hedging backtest on an index or single-stock futures contract — every ladder rung added, every call hedge roll, and the futures contract's own monthly rollover, replayed from historical data.",
            topics: [
              "Underlying TBD: index futures (e.g., NQ or ES) or a single-stock futures contract",
              "Ladder rung events: each futures contract added on confirmed momentum, hedge placed at calls_per_future ratio",
              "Call hedge rolls: weekly roll events showing down/out/up path taken and premium collected",
              "Futures rollover: the monthly futures contract roll and how it affects the ladder accounting",
              "End-of-period summary: total premium collected, unrealised futures P&L, and net position",
            ],
            videoSrc: null,
          },
        ],
      },
      {
        series: "Get Started",
        color: "#f7b924",
        icon: "⚙️",
        description: "Now that you understand the strategy: import a bare-minimum working rule set, backtest it, then practice risk-free before you go live.",
        items: [
          {
            id: "S02-FPH", title: "Importing Your Futures Pyramid Hedging Starter Rules", duration: "~2 min", priority: "P1",
            desc: "The same one-click Getting Started bundle as Pyramid Hedging, sized for Futures Pyramid Hedging's calls-only hedge and two independent expiry cycles — import it before you build your first ladder rung.",
            topics: [
              "The Futures Pyramid Hedging template category: pre-selected, pre-expanded, ready to import as-is",
              "Why the bundle skips CSP rules entirely — futures ladders don't carry a put leg",
              "Covering both expiry clocks: weekly call-roll rules and the futures contract's own monthly rollover",
              "Confirming your rules are ACTIVE in the Rule Builder after import",
            ],
            videoSrc: null,
          },
          {
            id: "S03-FPH", title: "Backtesting Your Futures Pyramid Hedging Starter Rules", duration: "~2 min", priority: "P1",
            desc: "The same Backtest page, run against a futures or single-stock futures contract instead of equity shares — see every ladder rung, every call-hedge roll, and the monthly futures rollover play out against real historical data.",
            topics: [
              "Picking a futures contract (index or single-stock) and date range on the Backtest Config page",
              "Reading the Adjustments Log: ladder rungs, call rolls, and the futures rollover, each with the rule that fired it",
              "How the synthetic-stock combo (for underlyings with no listed futures) shows up differently in the log",
              "The P&L view: unrealised futures P&L plus collected call premium, side by side",
            ],
            videoSrc: null,
          },
          {
            id: "S04-FPH", title: "Practicing Futures Pyramid Hedging in Demo Mode", duration: "~2 min", priority: "P1",
            desc: "Your demo account replays real futures market data at the same pace a live account runs on — including both expiry clocks — so you see ladder rungs, call rolls, and the monthly rollover fire the way they would in real trading, before any real money or margin is involved.",
            topics: [
              "Same ~1-day-old replay, same 5-minute cycle as Pyramid Hedging's demo mode",
              "Watching two independent expiry clocks tick down at once: the weekly call hedge and the monthly futures rollover",
              "Timestamps just show in your browser's local time — nothing to configure",
              "Demo provisioning is set up for you today (not yet self-service) — reach out if you need a fresh demo account",
            ],
            videoSrc: null,
          },
        ],
      },
    ],
  },

  /* ─────────────────────────────────────────────────────────────────────
   * 4 — WEEKLY INDEX SHORT STRADDLE (advanced, intraday-managed on indices)
   * ───────────────────────────────────────────────────────────────────── */
  {
    series: "Weekly Index Short Straddle",
    color: "#f59e0b",
    icon: "🎯",
    // Rewritten 2026-09-05: WISS is V3 (intraday-only) — no multi-day holding.
    // The previous description here matched V2 (Monday open, Friday close,
    // rolls/recovery/profit-target), which no longer exists. See
    // tutorials/scripts/wiss/wiss_intro.md and wiss_settings_guide.md.
    description: "An actively-managed intraday options-selling strategy on indices — opens a fresh ATM straddle on SPX every trading day, re-centers if the index drifts, and force-closes before the close, every day, with no multi-day holding. Same start-to-finish shape as the other strategies: concept, real examples, backtests, then the hands-on steps to import rules, backtest, and practice before going live.",
    children: [
      {
        series: "Strategy Guide",
        color: "#f59e0b",
        icon: "📘",
        description: "How WISS works and every setting explained.",
        items: [
          {
            // Rewritten 2026-09-05 for V3 — see tutorials/scripts/wiss/wiss_intro.md
            // for the full correction (this entry previously described V2's
            // multi-day-hold design, which no longer exists).
            // Produced 2026-09-05: screenshot_zoom capture on the real app
            // (neha_chopra demo account — Tradier sandbox has no working token
            // on this deployment, so live SPX/WISS builds aren't currently
            // possible here; data seeded directly via
            // backend/scripts/seed_wiss_intro_video.py, screens captured via
            // remotion/capture_wiss_intro.mjs). Along the way, found the real
            // Strategy Manage page has a dead-code bug for WISS: the layout
            // switch's first condition includes WEEKLY_INDEX_SHORT_STRADDLE
            // in the DHSS branch, so WISS's own dedicated layout block further
            // down is unreachable — filed as a known issue, not fixed here
            // (out of scope for a video script pass). Real duration 2:16, not
            // the earlier ~2 min estimate.
            id: "WISS-I", title: "Weekly Index Short Straddle: Introduction", duration: "2:16", priority: "P0",
            desc: "How WISS works: selling an ATM SPX call and put fresh every trading day, re-centering if SPX drifts past the strike, and force-closing before the close — no multi-day holding, no rolls, no recovery logic, no profit target.",
            topics: [
              "SPX weekly options: European-style, cash-settled, $5 strike grid",
              "Daily entry at open_time (default 09:35 ET): ATM strike found automatically; call + put sold at the same strike, same expiry",
              "Intraday-only: strike-cross closes and reopens the whole straddle same-day; end-of-day force-close every day, any P&L",
              "Low-premium safeguard: if either leg's LTP decays too far, close and reopen at next week's expiry",
              "What V3 deliberately removed: single-leg rolling, recovery-roll repositioning, and profit-target early exit",
            ],
            videoSrc: "/tutorials/wiss/wiss_intro.mp4",
          },
          {
            // Rewritten 2026-09-05 for V3 — collapsed from 3 chapters to one
            // video; see tutorials/scripts/wiss/wiss_settings_guide.md. V3 has
            // only 5 settings total, not enough surface area to split further.
            id: "WISS-S", title: "Weekly Index Short Straddle: Settings Guide", duration: "~2:30", priority: "P1",
            desc: "A complete walkthrough of all five WISS settings in one video: the daily trading window (open, cutoff, force-close times), lot sizing, and the low-premium safeguard.",
            topics: [
              "The daily trading window: open_time (default 09:35 ET), adjustment_cutoff_time (default 14:30 ET), eod_close_time (default 15:45 ET)",
              "Strike selection is automatic (nearest $5 to live SPX) — lot size (call+put pairs per open) is the one sizing lever you control",
              "min_leg_premium (default $50): closes and reopens at next week's expiry when a leg's premium decays too far",
              "skip_expiry_day_open: opens next week's contracts instead when today is itself the nearest expiry",
            ],
            videoSrc: null,
          },
        ],
      },
      {
        series: "Trading Examples",
        color: "#f59e0b",
        icon: "🧪",
        description: "Real SPX accounts, before/after comparisons for the settings that matter most.",
        items: [
          {
            // Rewritten 2026-09-05 for V3 — "standard roll boundary or
            // upper_target" no longer exist as settings (see WISS-S above).
            id: "TE-WISS01", title: "Trading Example: Weekly Index Short Straddle — Coming Soon", duration: "~2 min", priority: "P2",
            desc: "A real SPX account showing how one WISS setting changes the outcome — live-verified before/after numbers, same format as the Pyramid Hedging Trading Examples.",
            topics: [
              "Setting TBD: likely adjustment_cutoff_time or min_leg_premium, mirroring the stock-strategy examples",
              "Same account, same day, one toggle changed",
              "Real before/after numbers — no seeded or hypothetical data",
            ],
            videoSrc: null,
          },
        ],
      },
      {
        series: "Backtests",
        color: "#f59e0b",
        icon: "📊",
        // Rewritten 2026-09-05 for V3 — WISS resets fresh every day (no
        // multi-day holding), so a backtest is naturally framed around one
        // trading day, not one week. See tutorials/scripts/backtest/wiss/
        // bt_wiss_placeholder.md (also rewritten) for the full checklist.
        description: "Real historical SPX price data, a full trading day replayed.",
        items: [
          {
            id: "BT-WISS01", title: "Backtest: Weekly Index Short Straddle — Coming Soon", duration: "~3 min", priority: "P2",
            desc: "A real WISS backtest on SPX — one trading day fully replayed: the morning open, an intraday strike-cross re-center or two as SPX drifts, and the automatic end-of-day close.",
            topics: [
              "Day selected: ideally SPX drifts far enough intraday to trigger at least one strike-cross close + reopen, then settles",
              "Morning open_time entry: ATM strike on the $5 grid, combined straddle premium collected",
              "Strike-cross event: the straddle closes and immediately reopens at the new ATM strike — same day",
              "End-of-day force-close: final buyback cost for both legs, net daily P&L",
              "Return in $ per lot — SPX contract at 100× multiplier",
            ],
            videoSrc: null,
          },
        ],
      },
      {
        series: "Get Started",
        color: "#f7b924",
        icon: "⚙️",
        description: "Now that you understand the strategy: import a bare-minimum working rule set, backtest it, then practice risk-free before you go live.",
        items: [
          {
            // Rewritten 2026-09-05 for V3 — no ladder/accumulation rules note
            // still holds, but the rule set itself changed completely (see
            // wiss_settings_guide.md): no standard roll, recovery roll, or
            // upper-target exit anymore.
            id: "S02-WISS", title: "Importing Your Weekly Index Short Straddle Starter Rules", duration: "~2 min", priority: "P1",
            desc: "The Getting Started bundle for WISS — daily open, intraday strike-cross re-center, end-of-day force-close, and the low-premium safeguard — imported in one click before your first trading day.",
            topics: [
              "The Weekly Index Short Straddle template category: pre-selected, pre-expanded, ready to import as-is",
              "What's included: DAILY_OPEN_STRADDLE, STRIKE_CROSS_CLOSE, EOD_FORCE_CLOSE, LOW_PREMIUM_ROLL_TO_NEXT_EXPIRY",
              "Why WISS's bundle has no ladder or accumulation rules — it's a single fresh position each day, not a built-up one",
              "Confirming your rules are ACTIVE in the Rule Builder before the next trading day",
            ],
            videoSrc: null,
          },
          {
            // Rewritten 2026-09-05 for V3 — a backtest run covers one trading
            // day, not one week (WISS holds nothing overnight).
            id: "S03-WISS", title: "Backtesting Your Weekly Index Short Straddle Starter Rules", duration: "~2 min", priority: "P1",
            desc: "Run the exact rule set you just imported against a real historical SPX trading day — the open, any strike-cross re-centers that fired, and the end-of-day close — and read the Adjustments Log to see exactly why.",
            topics: [
              "Picking a day (any historical trading day) on the Backtest Config page",
              "Reading the Adjustments Log: the daily open, any strike-cross close + reopens, and the end-of-day force-close",
              "Why a single backtest run only ever covers one day — WISS opens and closes fresh every day",
              "The P&L view: premium collected vs. any re-center costs vs. final close, net for the day",
            ],
            videoSrc: null,
          },
          {
            // Rewritten 2026-09-05 for V3 — demo mode shows a daily cycle
            // repeating, not one continuous Monday-to-Friday hold.
            id: "S04-WISS", title: "Practicing Weekly Index Short Straddle in Demo Mode", duration: "~2 min", priority: "P1",
            desc: "Your demo account replays real SPX market data at real 5-minute pacing, so you see the straddle open, re-center if SPX drifts, and force-close the way it would on a live account — before any real money is involved.",
            topics: [
              "Same ~1-day-old replay, same 5-minute cycle as the other strategies' demo mode",
              "Watching a full daily cycle: open_time through eod_close_time, repeating fresh every trading day",
              "Timestamps just show in your browser's local time — nothing to configure",
              "Demo provisioning is set up for you today (not yet self-service) — reach out if you need a fresh demo account",
            ],
            videoSrc: null,
          },
        ],
      },
    ],
  },

  /* ─────────────────────────────────────────────────────────────────────
   * 5 — GO LIVE (shared — identical regardless of strategy; two paths:
   *     SnapTrade for most users, a self-hosted gateway for advanced ones)
   * ───────────────────────────────────────────────────────────────────── */
  {
    series: "Go Live",
    color: "#16a34a",
    icon: "🔌",
    description: "Comfortable with how your strategy behaves? Connect your real brokerage account and trade for real — same rules, same rule engine, real orders. Applies the same way regardless of which strategy you're running. Two paths, depending on your setup.",
    children: [
      {
        series: "Via SnapTrade",
        color: "#16a34a",
        icon: "🔌",
        description: "The default path for almost everyone: one OAuth login covers 35+ brokerages, no credentials ever typed into the platform.",
        items: [
          {
            id: "01B", title: "Connecting Your Brokerage", duration: "2:21 min", priority: "P0",
            desc: "Ready to trade for real? The platform can't do anything on your behalf until it can see your brokerage — positions, fills, balances. A single SnapTrade OAuth connection covers 35+ brokerages; you never type a credential into the platform itself.",
            topics: [
              "Why a connected brokerage is required before any strategy can be built or activated",
              "One login covers 35+ brokerages — Alpaca, Schwab, Fidelity, E-Trade, Robinhood, and more",
              "The SnapTrade OAuth flow: you authorize through your broker's own login, not ours",
              "Reading connection status: Read-only vs. Trading Enabled authorization badges",
            ],
            videoSrc: "/tutorials/platform/01b-brokerage.mp4",
          },
        ],
      },
      {
        series: "Advanced: Your Own Broker Gateway",
        color: "#64748b",
        icon: "🛠️",
        description: "For users who'd rather run their own execution gateway (e.g. IBKR) instead of SnapTrade: portfolio data is pulled from your own server, and approved trades are sent there for execution. Optional — most users won't need this.",
        items: [
          {
            id: "S06", title: "Connecting Your Own Gateway", duration: "~3 min", priority: "P2",
            desc: "How to configure a self-hosted broker execution gateway from the Brokerage page: your server's URL, an account reference, and a signing secret — plus where to download the full API reference to build or verify your own gateway.",
            topics: [
              "When to use this instead of SnapTrade: full control over execution, your own infrastructure",
              "Configuring base URL, account reference, and signing secret on the Brokerage page",
              "Downloading the API reference (read contract, write contract, auth, fill reconciliation)",
              "Current status: not yet verified against a live IBKR connection — recommended for paper/testing use first",
            ],
            videoSrc: null,
          },
        ],
      },
    ],
  },

  /* ─────────────────────────────────────────────────────────────────────
   * 6 — MONITORING, EXECUTION & SETTINGS (shared day-to-day workflow plus
   *     global configuration, cross-strategy)
   * ───────────────────────────────────────────────────────────────────── */
  {
    series: "Monitoring & Execution",
    color: "#30b1ff",
    icon: "👁️",
    description: "Your day-to-day workflow once you're live: reading positions, approving suggestions, staying informed with price alerts, and the global account configuration behind it all. Applies the same way across all three strategies.",
    items: [
      {
        id: "06", title: "Monitoring Positions & Greeks", duration: "2:23 min", priority: "P1",
        desc: "How to read your live option positions table, understand the delta/theta/IV columns, and track unrealised vs potential P&L in real time.",
        topics: [
          "Options positions table: what each column means (delta, theta, IV, unrealised, ITM indicator)",
          "How delta changes as the stock moves and what it means for your hedge percentage",
          "Unrealised P&L vs potential P&L: how they differ and which one matters for the rule engine",
          "Using the strategy summary view to see all active positions at a glance",
        ],
        videoSrc: "/tutorials/platform/06-monitoring.mp4",
      },
      {
        id: "07", title: "The Automation Approval Flow", duration: "2 min", priority: "P0",
        desc: "How rule-generated suggestions become executed trades — the full flow from rule fire through suggestion card to filled order.",
        topics: [
          "Rule fire → suggestion card: what triggers a card to appear and what it shows",
          "Approving a suggestion: what happens next and how long order execution takes",
          "Snooze vs reject: how each affects the cooldown and when you'd choose each",
          "Order queue: tracking pending, executing, and filled orders",
          "Execution history: the permanent audit trail for every decision",
        ],
        // Cache-busted (2026-09-05 re-render) — nginx serves /tutorials/ with
        // Cache-Control: max-age=86400 and the filename never changes between
        // re-renders, so without a version param browsers keep playing the
        // stale cached copy for up to 24h after the file on disk is replaced.
        videoSrc: "/tutorials/platform/07-approval.mp4?v=1788591600",
      },
      {
        id: "08", title: "Setting Up Price Alerts", duration: "90s", priority: "P2",
        desc: "Configure Telegram and email price threshold alerts on any tracked symbol so you're notified the moment a stock hits a level you care about.",
        topics: [
          "Adding a price alert: choosing a symbol, direction (above/below), and threshold",
          "Telegram and email notification channels: how to connect and test each",
          "How alerts interact with the rule engine — alerts notify, rules act",
          "Alert history: reviewing past firings and adjusting thresholds",
        ],
        videoSrc: "/tutorials/platform/08-alerts.mp4",
      },
      {
        id: "08B", title: "Notification Settings", duration: "1:38 min", priority: "P1",
        desc: "Price Alerts fire on a symbol crossing a threshold you set. Notification Settings is different — it controls how the platform reaches you when the system itself does something: a new suggestion, an order fill, an order failure, or a critical error.",
        topics: [
          "Two channels: Email and Telegram, each with its own toggle and test button",
          "Email: enter your address, click Send Test Email for instant confirmation",
          "Telegram: message @userinfobot to get your numeric Chat ID, paste it in, then Send Test Message",
          "Why this is a separate page from Price Alerts — system events vs. price thresholds",
        ],
        videoSrc: "/tutorials/platform/10-notifications.mp4",
      },
      {
        id: "09", title: "Configuring Your Account", duration: "2 min", priority: "P1",
        desc: "A walkthrough of all global account settings — the configuration that applies across all your strategies, not just individual ones.",
        topics: [
          "Global delta thresholds: the account-level bounds that all strategies inherit",
          "Adjustment type: how to set whether the system suggests or automatically adjusts",
          "Broker account setup: connecting Tradier, verifying credentials, and testing connectivity",
          "Telegram and email notifications: enabling alerts and approval reminders",
          "Subscription plan limits: max strategies, max accounts, and cycle frequency",
        ],
        videoSrc: "/tutorials/platform/09-settings.mp4",
      },
    ],
  },

  /* ─────────────────────────────────────────────────────────────────────
   * 7 — RULES DEEP DIVE (shared, cross-strategy, beyond the starter bundle)
   * ───────────────────────────────────────────────────────────────────── */
  {
    series: "Rules Deep Dive",
    color: "#f7b924",
    icon: "🔍",
    description: "Beyond the starter bundle: building custom rules from scratch, and background on the template system. Applies the same way across all three strategies.",
    items: [
      {
        id: "04", title: "Creating Rules from Templates", duration: "2 min", priority: "P1",
        desc: "Note: this video describes an earlier version of the template import flow (whole-template-only import). The template browser has since been redesigned to let you browse by category and pick individual rules — see \"Importing the Getting Started Rule Set\" (in each strategy's Get Started group) for the current flow. An updated version of this video is in progress.",
        topics: [
          "The original default Pyramid Hedging template concept",
          "Rule groups explained: Roll Management, Income First Mode, Exit Options, Expiry Management",
          "Priority order: why some rules must rank higher than others",
          "(Superseded) whole-template activation — the current UI imports by category and individual rule instead",
        ],
        videoSrc: "/tutorials/platform/04-templates.mp4",
      },
      {
        id: "05", title: "Building Custom Rules", duration: "2.5 min", priority: "P1",
        desc: "Build advanced IF/THEN rules from scratch using the visual rule editor — custom conditions, groups, cooldowns, and action parameters.",
        topics: [
          "Adding conditions: picking a fact, operator, and threshold value",
          "AND/OR logic: combining multiple conditions for precise triggers",
          "Setting action type and action-specific parameters",
          "Group, priority, and cooldown configuration — and why cooldowns matter for stability",
          "Saving and validating: how to confirm a rule will fire as expected",
        ],
        videoSrc: null,
      },
    ],
  },
];

/* ── generic node helpers (works at any depth) ────────────────────────────── */
function nodeItems(node) {
  if (node.items) return node.items;
  if (node.children) return node.children.flatMap(nodeItems);
  return [];
}

/* lookup: tutorialId → tutorial object (series = its immediate parent's name) */
export function getTutorialById(id) {
  function walk(nodes) {
    for (const node of nodes) {
      if (node.items) {
        const found = node.items.find(t => t.id === id);
        if (found) return { ...found, series: node.series };
      } else if (node.children) {
        const found = walk(node.children);
        if (found) return found;
      }
    }
    return null;
  }
  return walk(TUTORIALS);
}

/* lookup: route path → best tutorial id */
export function getTutorialForRoute(path) {
  const item = nodeItems({ children: TUTORIALS }).find(i => i.routes?.includes(path));
  return item ? item.id : null;
}

/* ── Component ──────────────────────────────────────────────────────────── */

// Chain of series names (root → leaf) that must all be open to reveal `id`.
// Series names are NOT unique across the tree (e.g. "Get Started" and
// "Trading Examples" repeat under each strategy), so accordion open/closed
// state is keyed by full path from the root, not by series name alone —
// see `pathKey` / `openKeysForId` below.
function openPathForId(id) {
  if (!id) return [];
  function walk(nodes, path) {
    for (const node of nodes) {
      const p = [...path, node.series];
      if (node.items) {
        if (node.items.some(i => i.id === id)) return p;
      } else if (node.children) {
        const found = walk(node.children, p);
        if (found) return found;
      }
    }
    return null;
  }
  return walk(TUTORIALS, []) || [];
}

// A stable, unique key for a node given its full ancestor path (root → node).
function pathKey(path) {
  return JSON.stringify(path);
}

// Every ancestor level's key that must be open to reveal `id` — e.g. for
// path ["Pyramid Hedging", "Get Started"] this returns the keys for both
// ["Pyramid Hedging"] and ["Pyramid Hedging", "Get Started"].
function openKeysForId(id) {
  const path = openPathForId(id);
  const keys = [];
  for (let i = 1; i <= path.length; i++) keys.push(pathKey(path.slice(0, i)));
  return keys;
}

function SectionHeader({ node, index, isOpen, onToggle, depth }) {
  const items = nodeItems(node);
  const readyCount = items.filter(i => i.videoSrc).length;
  const nested = depth > 0;
  return (
    <div
      onClick={onToggle}
      role="button"
      tabIndex={0}
      onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggle(); } }}
      style={{
        display: "flex", alignItems: "flex-start", gap: nested ? 10 : 12,
        marginBottom: isOpen ? (nested ? 12 : 16) : 0, paddingBottom: nested ? 8 : 12,
        borderBottom: `2px solid ${node.color}33`,
        cursor: "pointer", userSelect: "none",
      }}
    >
      <div style={{
        width: nested ? 28 : 36, height: nested ? 28 : 36, borderRadius: nested ? 8 : 10, flexShrink: 0,
        background: `linear-gradient(135deg, ${node.color}22, ${node.color}44)`,
        border: `1px solid ${node.color}33`,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: nested ? 13 : 18,
      }}>
        {node.icon}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: node.description ? 4 : 0, flexWrap: "wrap" }}>
          <span style={{
            fontSize: nested ? 10 : 12, color: "#999", flexShrink: 0,
            display: "inline-block", transition: "transform 0.15s",
            transform: isOpen ? "rotate(90deg)" : "rotate(0deg)",
          }}>
            ▶
          </span>
          <span style={{ fontSize: depth === 0 ? 15 : depth === 1 ? 13.5 : 12.5, fontWeight: 700, color: "#222" }}>
            {index != null ? `${index}. ` : ""}{node.series}
          </span>
          <span style={{
            fontSize: 11, color: node.color, fontWeight: 600,
            background: `${node.color}15`, borderRadius: 20, padding: "1px 8px",
            border: `1px solid ${node.color}33`,
          }}>
            {items.length} video{items.length !== 1 ? "s" : ""}
          </span>
          <span style={{
            fontSize: 11, color: "#aaa", fontWeight: 500,
            background: "#f5f5f5", borderRadius: 20, padding: "1px 8px",
            border: "1px solid #e8e8e8",
          }}>
            {readyCount} ready
          </span>
        </div>
        {node.description && (
          <p style={{ margin: 0, fontSize: nested ? 11.5 : 12, color: "#777", lineHeight: 1.5 }}>
            {node.description}
          </p>
        )}
      </div>
    </div>
  );
}

function TutorialCard({ item, node, highlightId, highlightRef, onPlay }) {
  const isHighlighted = item.id === highlightId;
  const isReady = Boolean(item.videoSrc);
  return (
    <div
      key={item.id}
      ref={isHighlighted ? highlightRef : null}
      style={{
        display: "flex", alignItems: "flex-start", gap: 14,
        background: isHighlighted ? `${node.color}0d` : "#fff",
        border: `1px solid ${isHighlighted ? node.color : "#eee"}`,
        borderLeft: `4px solid ${isHighlighted ? node.color : isReady ? node.color + "66" : "#ddd"}`,
        borderRadius: 10,
        padding: "12px 16px",
        opacity: isReady ? 1 : 0.78,
        transition: "border-color 0.2s, background 0.2s, opacity 0.2s",
        boxShadow: isHighlighted ? `0 2px 12px ${node.color}22` : "0 1px 3px rgba(0,0,0,0.04)",
      }}
      onMouseEnter={e => {
        if (!isHighlighted && isReady) {
          e.currentTarget.style.borderColor = `${node.color}66`;
          e.currentTarget.style.background = "#fafafa";
        }
      }}
      onMouseLeave={e => {
        if (!isHighlighted) {
          e.currentTarget.style.borderColor = isReady ? `${node.color}66` : "#ddd";
          e.currentTarget.style.background = "#fff";
        }
      }}
    >
      {/* step number + thumbnail */}
      <div style={{
        width: 56, height: 44, borderRadius: 8, flexShrink: 0,
        background: isReady
          ? `linear-gradient(135deg, ${node.color}22 0%, ${node.color}44 100%)`
          : "linear-gradient(135deg, #f0f0f0, #e0e0e0)",
        border: `1px solid ${isReady ? node.color + "33" : "#ddd"}`,
        display: "flex", alignItems: "center", justifyContent: "center",
        position: "relative", overflow: "hidden",
        fontSize: 18,
      }}>
        {isReady ? node.icon : "🔜"}
        <span style={{
          position: "absolute", bottom: 2, right: 3,
          fontSize: 9, fontWeight: 700,
          color: isReady ? node.color : "#aaa",
          background: "#fff", borderRadius: 3, padding: "0 3px",
        }}>
          {item.id}
        </span>
      </div>

      {/* text */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: isReady ? "#222" : "#666" }}>
            {item.title}
          </span>
          {isHighlighted && (
            <span style={{
              fontSize: 9, background: node.color, color: "#fff",
              borderRadius: 10, padding: "1px 6px", fontWeight: 700,
            }}>
              SUGGESTED
            </span>
          )}
          {!isReady && (
            <span style={{
              fontSize: 9, background: "#f0f0f0", color: "#888",
              borderRadius: 10, padding: "1px 6px", fontWeight: 600,
              border: "1px solid #ddd",
            }}>
              COMING SOON
            </span>
          )}
        </div>
        <div style={{
          fontSize: 12, color: "#777", lineHeight: 1.45, marginBottom: !isReady && item.topics?.length ? 6 : 0,
          overflow: isReady ? "hidden" : "visible",
          textOverflow: isReady ? "ellipsis" : "unset",
          display: isReady ? "-webkit-box" : "block",
          WebkitLineClamp: isReady ? 2 : undefined,
          WebkitBoxOrient: isReady ? "vertical" : undefined,
        }}>
          {item.desc}
        </div>
        {/* Coming soon: show full topic list so users know what's coming */}
        {!isReady && item.topics?.length > 0 && (
          <ul style={{ margin: "4px 0 0", paddingLeft: 16 }}>
            {item.topics.map((t, i) => (
              <li key={i} style={{ fontSize: 11, color: "#888", marginBottom: 2, lineHeight: 1.45 }}>
                {t}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* badges + action */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6, flexShrink: 0 }}>
        <div style={{ display: "flex", gap: 4 }}>
          <span style={{
            fontSize: 10, background: `${node.color}15`, color: isReady ? node.color : "#aaa",
            border: `1px solid ${isReady ? node.color + "33" : "#ddd"}`,
            borderRadius: 20, padding: "1px 7px", fontWeight: 600,
          }}>
            {item.duration}
          </span>
          {item.priority === "P0" && (
            <span style={{
              fontSize: 10, background: "#fff3cd", color: "#856404",
              border: "1px solid #ffc107", borderRadius: 20, padding: "1px 7px", fontWeight: 600,
            }}>
              Essential
            </span>
          )}
        </div>
        <button
          onClick={() => isReady && onPlay({ ...item, series: node.series })}
          disabled={!isReady}
          style={{
            background: isReady ? node.color : "#e0e0e0",
            color: isReady ? "#fff" : "#aaa",
            border: "none", borderRadius: 6,
            fontSize: 11, fontWeight: 600,
            padding: "5px 14px",
            cursor: isReady ? "pointer" : "not-allowed",
            whiteSpace: "nowrap",
            opacity: isReady ? 1 : 0.7,
          }}
          onMouseEnter={e => { if (isReady) e.currentTarget.style.opacity = "0.85"; }}
          onMouseLeave={e => { if (isReady) e.currentTarget.style.opacity = "1"; }}
        >
          {isReady ? "▶ Watch" : "Coming Soon"}
        </button>
      </div>
    </div>
  );
}

// Recursive: renders a node's header, and (if open) either its child nodes
// or its leaf tutorial cards. `parentPath` is the chain of ancestor series
// names (root → this node's parent); combined with node.series it forms the
// full-path key used for accordion open/closed state (see `pathKey` above).
function TreeNode({ node, index, depth, parentPath, openSections, toggleSection, highlightId, highlightRef, onPlay }) {
  const path = [...parentPath, node.series];
  const key = pathKey(path);
  const isOpen = openSections.has(key);
  return (
    <div style={{
      marginBottom: isOpen ? (depth === 0 ? 40 : 20) : (depth === 0 ? 12 : 4),
      marginLeft: depth > 0 ? 20 : 0,
    }}>
      <SectionHeader
        node={node}
        index={index}
        isOpen={isOpen}
        onToggle={() => toggleSection(key)}
        depth={depth}
      />

      {isOpen && node.children && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {node.children.map(child => (
            <TreeNode
              key={child.series}
              node={child}
              index={null}
              depth={depth + 1}
              parentPath={path}
              openSections={openSections}
              toggleSection={toggleSection}
              highlightId={highlightId}
              highlightRef={highlightRef}
              onPlay={onPlay}
            />
          ))}
        </div>
      )}

      {isOpen && node.items && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: depth > 0 ? 12 : 0 }}>
          {node.items.map(item => (
            <TutorialCard
              key={item.id}
              item={item}
              node={node}
              highlightId={highlightId}
              highlightRef={highlightRef}
              onPlay={onPlay}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function TutorialTree({ highlightId, onPlay }) {
  const highlightRef = useRef(null);

  // Accordion: default-collapsed, except the section(s) containing the current/highlighted video.
  const [openSections, setOpenSections] = useState(() => new Set(openKeysForId(highlightId)));

  useEffect(() => {
    const keys = openKeysForId(highlightId);
    if (!keys.length) return;
    setOpenSections(prev => {
      const next = new Set(prev);
      let changed = false;
      for (const k of keys) if (!next.has(k)) { next.add(k); changed = true; }
      return changed ? next : prev;
    });
  }, [highlightId]);

  useEffect(() => {
    if (highlightRef.current) {
      setTimeout(() => highlightRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 120);
    }
  }, [highlightId]);

  const toggleSection = (key) => {
    setOpenSections(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const allItems = nodeItems({ children: TUTORIALS });
  const totalVideos = allItems.length;
  const readyVideos = allItems.filter(i => i.videoSrc).length;

  return (
    <div style={{ padding: "24px 28px", maxWidth: 900, margin: "0 auto" }}>

      {/* page header */}
      <div style={{ marginBottom: 32 }}>
        <h2 style={{ margin: "0 0 6px", fontSize: 22, fontWeight: 800, color: "#1a1a2e" }}>
          Video Library
        </h2>
        <p style={{ margin: "0 0 10px", fontSize: 13, color: "#888" }}>
          {readyVideos} videos available · {totalVideos - readyVideos} coming soon · Start with Platform Basics, then open the strategy you're running — each one goes concept → comparisons → real examples → backtests → hands-on Get Started
        </p>
        <div style={{
          display: "flex", alignItems: "center", gap: 8,
          background: "#f0f7ff", border: "1px solid #cfe4ff", borderRadius: 8,
          padding: "8px 12px", fontSize: 12, color: "#3b5f8a",
        }}>
          💬 Stuck on anything here? Click the chat bubble in the bottom-right corner of any page — it's there the whole time.
        </div>
      </div>

      {TUTORIALS.map((node, idx) => (
        <TreeNode
          key={node.series}
          node={node}
          index={idx + 1}
          depth={0}
          parentPath={[]}
          openSections={openSections}
          toggleSection={toggleSection}
          highlightId={highlightId}
          highlightRef={highlightRef}
          onPlay={onPlay}
        />
      ))}
    </div>
  );
}
